import { create } from 'zustand';
import {
  buildReactionRecord,
  nextTrackReaction,
  reactionScopeKey,
  trackOccurrenceKey,
} from '../utils/trackReactions';

export const TRACK_REACTION_STORAGE_KEY = 'rhythm-e.trackReactions.v1';

function readAll() {
  try {
    const raw = localStorage.getItem(TRACK_REACTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  try {
    localStorage.setItem(TRACK_REACTION_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

const useReactionStore = create((set, get) => ({
  scopeKey: null,
  context: 'event',
  contextId: null,
  userId: null,
  byOccurrence: {},

  bindScope: ({ context = 'event', contextId = null, userId = null } = {}) => {
    const scopeKey = reactionScopeKey({ context, contextId, userId });
    const stored = scopeKey ? (readAll()[scopeKey] || {}) : {};
    set({
      scopeKey,
      context,
      contextId,
      userId,
      byOccurrence: stored && typeof stored === 'object' ? stored : {},
    });
  },

  reactionFor: (track) => {
    const key = trackOccurrenceKey(track);
    if (!key) return null;
    return get().byOccurrence[key] || null;
  },

  setReaction: (track, tappedType, { live = false } = {}) => {
    const { context, contextId, userId, scopeKey } = get();
    const key = trackOccurrenceKey(track);
    if (!key || !scopeKey) return null;

    const current = get().byOccurrence[key]?.type || null;
    const nextType = nextTrackReaction(current, tappedType);
    if (!nextType) return current;
    if (nextType === current) return current;

    const nextByOccurrence = { ...get().byOccurrence };
    const record = buildReactionRecord({
      type: nextType,
      track,
      context,
      contextId,
      userId,
      live,
    });
    if (!record) return current;
    nextByOccurrence[key] = record;

    set({ byOccurrence: nextByOccurrence });
    const all = readAll();
    all[scopeKey] = nextByOccurrence;
    writeAll(all);
    return nextType;
  },

  reset: () => set({
    scopeKey: null,
    context: 'event',
    contextId: null,
    userId: null,
    byOccurrence: {},
  }),
}));

export default useReactionStore;
